import Ajv, { ValidateFunction } from 'ajv';
import { A2uiComponentEntry, SpaceWire } from 'types/space';
import { CompositionDefinition, CompositionExample, CompositionInterface, CompositionPropDef, CompositionRef } from 'types/composition';
import { Surface, getCatalogJsonSchema } from '@center-platform/component-catalog';
import { normalizeCompositionSlots } from '../normalizeCompositionSlots';
import { ConformanceFinding, makeFinding } from './findings';

/** Looks up a referenced composition's declared interface; `undefined` means "not a composition reference" (a catalog primitive). */
export type InterfaceResolver = (ref: CompositionRef) => CompositionInterface | undefined;

const JSON_TYPE_CHECKS: Record<string, (value: unknown) => boolean> = {
  string: (value) => typeof value === 'string',
  boolean: (value) => typeof value === 'boolean',
  number: (value) => typeof value === 'number',
  'string[]': (value) => Array.isArray(value) && value.every((item) => typeof item === 'string'),
};

/**
 * A caller-supplied prop value that is a live binding (`{ $bind: '/sphere/...' }` or similar) is
 * not a literal — its resolved type is an L3 concern (schema resolver), never checked here.
 */
function isBindingExpression(value: unknown): boolean {
  return typeof value === 'object' && value !== null && !Array.isArray(value) && typeof (value as { $bind?: unknown }).$bind === 'string';
}

function callerFieldsOf(entry: A2uiComponentEntry): Record<string, unknown> {
  const { id: _id, component: _component, componentVersion: _componentVersion, ...rest } = entry as A2uiComponentEntry & {
    componentVersion?: number;
  };
  return rest;
}

/**
 * L2 caller-side contract check: does a document's usage of a composition reference (`entry`)
 * satisfy the composition's declared `interface`? Required/type/enum-checks a literal prop value;
 * a bound value is left to L3. Slot fill is inferred from array-shaped caller fields (the same
 * shape `compositionInterfaceToJsonSchema` generates for a slot).
 */
export function checkCallerContract(entry: A2uiComponentEntry, iface: CompositionInterface, path: string): ConformanceFinding[] {
  const findings: ConformanceFinding[] = [];
  const callerFields = callerFieldsOf(entry);
  const declaredProps = iface.props ?? {};
  const declaredSlots = normalizeCompositionSlots(iface.slots);
  const declaredSlotNames = new Set(declaredSlots.map((slot) => slot.name));
  const ref: ConformanceFinding['ref'] = { systemName: entry.component, version: entry.componentVersion as number | undefined };

  for (const [propName, propDef] of Object.entries(declaredProps)) {
    const hasField = Object.prototype.hasOwnProperty.call(callerFields, propName);
    if (propDef.required && !hasField) {
      findings.push(
        makeFinding('PROP_REQUIRED_MISSING', 'L2', 'error', `Required prop "${propName}" was not supplied to composition "${entry.component}"`, {
          path: `${path}/${propName}`,
          ref,
        })
      );
      continue;
    }
    if (!hasField) continue;

    const value = callerFields[propName];
    if (isBindingExpression(value)) continue;

    if (propDef.enum && typeof value === 'string' && !propDef.enum.includes(value)) {
      findings.push(
        makeFinding(
          'PROP_ENUM_VIOLATION',
          'L2',
          'error',
          `Prop "${propName}" value "${value}" is not one of [${propDef.enum.join(', ')}] declared by composition "${entry.component}"`,
          { path: `${path}/${propName}`, ref }
        )
      );
      continue;
    }

    if (!matchesDeclaredType(value, propDef)) {
      findings.push(
        makeFinding(
          'PROP_TYPE_MISMATCH',
          'L2',
          'error',
          `Prop "${propName}" on composition "${entry.component}" expects type "${propDef.type}" but received ${typeof value}`,
          { path: `${path}/${propName}`, ref }
        )
      );
    }
  }

  for (const slot of declaredSlots) {
    const value = callerFields[slot.name];
    const filled = Array.isArray(value) && value.length > 0;
    if (!filled) {
      findings.push(
        makeFinding(
          'SLOT_UNFILLED',
          'L2',
          slot.required ? 'error' : 'warning',
          `Slot "${slot.name}" declared by composition "${entry.component}" received no children`,
          { path: `${path}/${slot.name}`, ref }
        )
      );
    }
  }

  for (const fieldName of Object.keys(callerFields)) {
    if (declaredProps[fieldName] || declaredSlotNames.has(fieldName)) continue;
    const value = callerFields[fieldName];
    if (Array.isArray(value)) {
      findings.push(
        makeFinding('SLOT_UNDECLARED', 'L2', 'warning', `Composition "${entry.component}" does not declare a slot named "${fieldName}"`, {
          path: `${path}/${fieldName}`,
          ref,
        })
      );
    } else {
      findings.push(
        makeFinding('PROP_UNDECLARED', 'L2', 'warning', `Composition "${entry.component}" does not declare a prop named "${fieldName}"`, {
          path: `${path}/${fieldName}`,
          ref,
        })
      );
    }
  }

  return findings;
}

/**
 * L2 story-conformance check (SC11): does each saved story's `props` satisfy the composition's own
 * declared interface? A story is a synthetic caller usage — the same `checkCallerContract` rules a
 * referencing composition's usage would be checked against — since a story's `props` are exactly
 * that: a set of caller-supplied field values with no embedding document of their own. Run at
 * publish only (not on every draft save), so authors can iterate on an interface and its stories
 * out of lockstep until they commit to a version.
 */
export function checkStoryConformance(
  examples: readonly CompositionExample[] | null | undefined,
  iface: CompositionInterface,
  systemName: string
): ConformanceFinding[] {
  if (!examples || examples.length === 0) return [];

  return examples.flatMap((example, index) => {
    const syntheticEntry: A2uiComponentEntry = { id: `story:${index}`, component: systemName, ...example.props };
    return checkCallerContract(syntheticEntry, iface, `examples/${index}/props`).map((finding) => ({
      ...finding,
      message: `Story "${example.name}": ${finding.message}`,
    }));
  });
}

function matchesDeclaredType(value: unknown, propDef: CompositionPropDef): boolean {
  const check = JSON_TYPE_CHECKS[propDef.type];
  // Types outside the closed primitive set (e.g. `BoundValue`, inline object shapes) have no
  // structured grammar here — `compositionInterfaceToJsonSchema` treats them permissively for the
  // same reason; only the primitives we can actually check are enforced.
  return check ? check(value) : true;
}

/**
 * L2 check: a `SpaceWire`'s `from` component, when it is itself a composition reference, must
 * declare `wire.action` in its own `interface.emits` — a wire cannot route an action the source
 * composition never promises to raise. Wires whose `from` does not resolve to any entry, or
 * resolves to a catalog primitive (not a composition), are out of scope here (L1 / not applicable).
 */
export function checkWireEmitTargets(
  wires: readonly SpaceWire[],
  entriesById: ReadonlyMap<string, A2uiComponentEntry>,
  resolveInterface: InterfaceResolver,
  pathPrefix = 'wires'
): ConformanceFinding[] {
  const findings: ConformanceFinding[] = [];

  wires.forEach((wire, index) => {
    const sourceEntry = entriesById.get(wire.from);
    if (!sourceEntry) return;

    const iface = resolveInterface({ systemName: sourceEntry.component, version: sourceEntry.componentVersion as number | undefined });
    if (!iface) return;

    if (!(iface.emits ?? []).includes(wire.action)) {
      findings.push(
        makeFinding(
          'EMIT_UNDECLARED_TARGET',
          'L2',
          'error',
          `Wire "${wire.id}" routes action "${wire.action}" from "${wire.from}", but composition "${sourceEntry.component}" does not declare it in interface.emits`,
          { path: `${pathPrefix}/${index}`, ref: { systemName: sourceEntry.component, version: sourceEntry.componentVersion as number | undefined } }
        )
      );
    }
  });

  return findings;
}

/** Collects every `/props/<name>` pointer bound anywhere in a component subtree. */
function collectBoundPropNames(components: readonly A2uiComponentEntry[]): Set<string> {
  const bound = new Set<string>();

  const visit = (value: unknown): void => {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      const bindPointer = (value as { $bind?: unknown }).$bind;
      if (typeof bindPointer === 'string' && bindPointer.startsWith('/props/')) {
        bound.add(bindPointer.slice('/props/'.length));
        return;
      }
      Object.values(value).forEach(visit);
      return;
    }
    if (Array.isArray(value)) {
      value.forEach(visit);
    }
  };

  components.forEach((entry) => {
    Object.entries(entry).forEach(([key, value]) => {
      if (key === 'id' || key === 'component') return;
      visit(value);
    });
  });

  return bound;
}

/** Collects every string value found anywhere in a component subtree — the proxy for "this action name is dispatched somewhere". */
function collectDispatchedStrings(components: readonly A2uiComponentEntry[]): Set<string> {
  const dispatched = new Set<string>();

  const visit = (value: unknown): void => {
    if (typeof value === 'string') {
      dispatched.add(value);
      return;
    }
    if (Array.isArray(value)) {
      value.forEach(visit);
      return;
    }
    if (value && typeof value === 'object') {
      Object.values(value).forEach(visit);
    }
  };

  components.forEach((entry) => {
    Object.entries(entry).forEach(([key, value]) => {
      if (key === 'id' || key === 'component') return;
      visit(value);
    });
  });

  return dispatched;
}

/**
 * L2 self-side check: does a composition's own subtree actually bind every prop, and dispatch
 * every emit, it declares in its `interface`? A declared-but-unbound prop is exactly the shape
 * that stores cleanly and expands to `undefined` today — `expandComposition` substitutes into
 * `/props/*` with no check that the pointer exists anywhere.
 */
export function checkSelfContract(definition: CompositionDefinition, systemName: string): ConformanceFinding[] {
  const findings: ConformanceFinding[] = [];
  const iface = definition.interface;
  const boundPropNames = collectBoundPropNames(definition.a2ui.components);
  const dispatchedStrings = collectDispatchedStrings(definition.a2ui.components);

  for (const propName of Object.keys(iface.props ?? {})) {
    if (!boundPropNames.has(propName)) {
      findings.push(
        makeFinding(
          'INTERFACE_PROP_UNBOUND',
          'L2',
          'error',
          `Composition "${systemName}" declares prop "${propName}" but its subtree never binds "/props/${propName}"`,
          { path: `interface/props/${propName}`, ref: { systemName } }
        )
      );
    }
  }

  for (const emitName of iface.emits ?? []) {
    if (!dispatchedStrings.has(emitName)) {
      findings.push(
        makeFinding(
          'INTERFACE_EMIT_UNUSED',
          'L2',
          'warning',
          `Composition "${systemName}" declares emit "${emitName}" but its subtree never dispatches it`,
          { path: 'interface/emits', ref: { systemName } }
        )
      );
    }
  }

  return findings;
}

// ---------------------------------------------------------------------------
// Primitive prop schema checks (catalog JSON-Schema via ajv)
// ---------------------------------------------------------------------------

/** `${catalogVersion}:${surface}` → one compiled validator per catalog component type. Compiled once, reused for every `verifyArtifact` call at that catalog version. */
const catalogValidatorCache = new Map<string, Map<string, ValidateFunction>>();

function getCatalogValidators(catalogVersion: number, surface: Surface): Map<string, ValidateFunction> {
  const cacheKey = `${catalogVersion}:${surface}`;
  const cached = catalogValidatorCache.get(cacheKey);
  if (cached) return cached;

  const schema = getCatalogJsonSchema({ surface }) as { definitions: Record<string, object> };
  const ajv = new Ajv({ allErrors: true, strict: false });
  const validators = new Map<string, ValidateFunction>();
  for (const [type, definitionSchema] of Object.entries(schema.definitions)) {
    validators.set(type, ajv.compile(definitionSchema));
  }

  catalogValidatorCache.set(cacheKey, validators);
  return validators;
}

/**
 * L2 check: a code-backed component's props against its catalog JSON-Schema entry — the check
 * `compositionInterfaceToJsonSchema` mirrors for compositions but which primitives never actually
 * ran through until now (a component's own catalog entry was descriptive metadata, not an
 * enforced contract). Returns no findings for a `component` type absent from the catalog for this
 * surface — that is a composition reference or an undeclared type, both out of scope here.
 */
export function checkPrimitivePropSchema(entry: A2uiComponentEntry, catalogVersion: number, surface: Surface, path: string): ConformanceFinding[] {
  const validate = getCatalogValidators(catalogVersion, surface).get(entry.component);
  if (!validate) return [];

  // `validate` is typed as a type predicate (`data is T`); assign the boolean result to a variable
  // rather than branching on `validate(entry)` directly, or TS narrows `entry` itself to `never`
  // in the else branch (T defaults to `unknown`, so `Exclude<A2uiComponentEntry, unknown>` is `never`).
  const isValid: boolean = validate(entry);
  if (isValid) return [];

  return (validate.errors ?? []).map((error) => {
    const suffix = error.instancePath ? error.instancePath.replace(/^\//, '') : '';
    return makeFinding(
      'PRIMITIVE_PROP_SCHEMA_VIOLATION',
      'L2',
      'error',
      `Component "${entry.id}" (${entry.component}) violates its catalog schema at "${error.instancePath || '/'}": ${error.message}`,
      { path: suffix ? `${path}/${suffix}` : path }
    );
  });
}
